import {
  fetchPushPublicKey,
  registerPushDevice,
  unregisterPushDevice,
} from '@/services/pushNotifications';
import { isIosDevice, isStandaloneDisplay } from './device';

export const PUSH_STATUS = Object.freeze({
  UNSUPPORTED: 'unsupported',
  IOS_NEEDS_INSTALL: 'ios-needs-install',
  DENIED: 'denied',
  UNAVAILABLE: 'unavailable',
  OFF: 'off',
  ON: 'on',
});

export const PUSH_OPT_IN_KEY = 'automy-push-optin';
export const PUSH_PROMPT_DISMISSED_KEY = 'automy-push-prompt-dismissed';
/** "Not now" stays quiet for two weeks. Asking again sooner trains people to block the site. */
export const PUSH_PROMPT_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000;
const REGISTRATION_WAIT_MS = 10000;
const STEP_TIMEOUT_MS = 5000;

const withTimeout = (promise, ms) =>
  Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
  ]);

export const isPushApiAvailable = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

/* ── Per-user opt-in on this browser ───────────────────────────────────── */

const readOptIns = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(PUSH_OPT_IN_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch (_) {
    return [];
  }
};

const writeOptIns = (ids) => {
  try {
    window.localStorage.setItem(PUSH_OPT_IN_KEY, JSON.stringify(ids));
  } catch (_) {
    /* private mode */
  }
};

export const hasPushOptIn = (userId) => !!userId && readOptIns().includes(String(userId));

const rememberOptIn = (userId) => {
  if (!userId || hasPushOptIn(userId)) return;
  writeOptIns([...readOptIns(), String(userId)]);
};

const forgetOptIn = (userId) => {
  if (!userId) return;
  writeOptIns(readOptIns().filter((id) => id !== String(userId)));
};

const readPromptDismissals = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(PUSH_PROMPT_DISMISSED_KEY) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (_) {
    return {};
  }
};

/** True while this user is still inside the cooldown after choosing "not now" or turning push off. */
export const isPushPromptDismissed = (userId, now = Date.now()) => {
  if (!userId) return true;
  const dismissedAt = Number(readPromptDismissals()[String(userId)]);
  return (
    Number.isFinite(dismissedAt) && dismissedAt > 0 && now - dismissedAt < PUSH_PROMPT_COOLDOWN_MS
  );
};

export const markPushPromptDismissed = (userId, now = Date.now()) => {
  if (!userId) return;
  try {
    const dismissals = readPromptDismissals();
    dismissals[String(userId)] = now;
    window.localStorage.setItem(PUSH_PROMPT_DISMISSED_KEY, JSON.stringify(dismissals));
  } catch (_) {
    /* private mode: the prompt can return next session */
  }
};

/* ── Keys and registration ─────────────────────────────────────────────── */

export const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
};

const sameApplicationServerKey = (subscription, keyBytes) => {
  const current = subscription?.options?.applicationServerKey;
  if (!current) return true;
  const currentBytes = new Uint8Array(current);
  if (currentBytes.length !== keyBytes.length) return false;
  return currentBytes.every((byte, index) => byte === keyBytes[index]);
};

/** The root-scope registration is tokenRefreshWorker.js; /apps/ has its own worker. */
const getRootRegistration = async () => {
  if (!isPushApiAvailable()) return null;
  const rootScope = `${window.location.origin}/`;
  try {
    const existing = await navigator.serviceWorker.getRegistration('/');
    if (existing?.scope === rootScope) return existing;
    const ready = await withTimeout(navigator.serviceWorker.ready, REGISTRATION_WAIT_MS);
    return ready?.scope === rootScope ? ready : null;
  } catch (_) {
    return null;
  }
};

const waitForActiveWorker = (registration) => {
  if (registration.active) return Promise.resolve(registration);
  const worker = registration.installing || registration.waiting;
  if (!worker) return Promise.reject(new Error('no-worker'));
  return withTimeout(
    new Promise((resolve) => {
      worker.addEventListener('statechange', () => {
        if (worker.state === 'activated') resolve(registration);
      });
    }),
    REGISTRATION_WAIT_MS,
  );
};

const readPublicKey = async () => {
  const response = await withTimeout(fetchPushPublicKey(), STEP_TIMEOUT_MS);
  const publicKey = typeof response === 'string' ? response : response?.publicKey;
  if (!publicKey) throw new Error('push-unavailable');
  return publicKey;
};

const subscribeAndRegister = async (registration) => {
  const keyBytes = urlBase64ToUint8Array(await readPublicKey());
  await waitForActiveWorker(registration);

  let subscription = await registration.pushManager.getSubscription();
  if (subscription && !sameApplicationServerKey(subscription, keyBytes)) {
    await subscription.unsubscribe();
    subscription = null;
  }
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: keyBytes,
    });
  }

  await withTimeout(registerPushDevice(subscription.toJSON()), STEP_TIMEOUT_MS);
  return subscription;
};

const removeSubscription = async (subscription) => {
  await withTimeout(unregisterPushDevice(subscription.endpoint), STEP_TIMEOUT_MS).catch(() => {});
  await withTimeout(subscription.unsubscribe(), STEP_TIMEOUT_MS).catch(() => {});
};

/* ── Public API ────────────────────────────────────────────────────────── */

/**
 * @param {string} userId
 * @returns {Promise<string>} one of PUSH_STATUS
 */
export const getPushSupport = async (userId) => {
  if (isIosDevice() && !isStandaloneDisplay()) return PUSH_STATUS.IOS_NEEDS_INSTALL;
  if (!isPushApiAvailable()) return PUSH_STATUS.UNSUPPORTED;
  if (Notification.permission === 'denied') return PUSH_STATUS.DENIED;

  const registration = await getRootRegistration();
  if (!registration) return PUSH_STATUS.UNSUPPORTED;

  try {
    const subscription = await registration.pushManager.getSubscription();
    return subscription && Notification.permission === 'granted' && hasPushOptIn(userId)
      ? PUSH_STATUS.ON
      : PUSH_STATUS.OFF;
  } catch (_) {
    return PUSH_STATUS.OFF;
  }
};

/**
 * Must run from a user gesture: it may show the browser permission prompt.
 * @returns {Promise<string>} resulting PUSH_STATUS
 */
export const enablePush = async (userId) => {
  if (isIosDevice() && !isStandaloneDisplay()) return PUSH_STATUS.IOS_NEEDS_INSTALL;
  if (!isPushApiAvailable()) return PUSH_STATUS.UNSUPPORTED;
  if (Notification.permission === 'denied') return PUSH_STATUS.DENIED;

  // Asked before any other await: Safari drops the user gesture across async work.
  const permission =
    Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (permission === 'denied') return PUSH_STATUS.DENIED;
  if (permission !== 'granted') return PUSH_STATUS.OFF;

  const registration = await getRootRegistration();
  if (!registration) return PUSH_STATUS.UNSUPPORTED;

  try {
    await subscribeAndRegister(registration);
  } catch (error) {
    if (error?.message === 'push-unavailable') return PUSH_STATUS.UNAVAILABLE;
    throw error;
  }
  rememberOptIn(userId);
  return PUSH_STATUS.ON;
};

/**
 * Removes this browser from the backend and from the push service. Never throws.
 * @param {{ userId?: string, forget?: boolean }} options forget=true also drops the user's opt-in
 */
export const disablePush = async ({ userId, forget = false } = {}) => {
  try {
    if (forget) forgetOptIn(userId);
    const registration = await getRootRegistration();
    const subscription = registration ? await registration.pushManager.getSubscription() : null;
    if (subscription) await removeSubscription(subscription);
  } catch (_) {
    /* logout and the toggle must not fail because of push */
  }
};

/** Bounded variant for logout, which must not wait on the network. */
export const disablePushWithin = (ms, options) =>
  Promise.race([disablePush(options), new Promise((resolve) => setTimeout(resolve, ms))]);

/**
 * After sign-in: re-register the subscription for a user who opted in, or drop
 * one left behind by another user whose session ended without a logout.
 * Never throws and never prompts.
 */
export const syncPushSubscription = async (userId) => {
  try {
    // Subscribe only in the installed app. pushManager.subscribe can raise the
    // notification permission dialog and dismiss Android's install sheet.
    if (!isStandaloneDisplay()) return;
    if (!userId || !isPushApiAvailable() || Notification.permission !== 'granted') return;
    const registration = await getRootRegistration();
    if (!registration) return;

    if (hasPushOptIn(userId)) {
      await subscribeAndRegister(registration);
      return;
    }
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) await removeSubscription(subscription);
  } catch (_) {
    /* best effort; the profile toggle shows the real state */
  }
};

/** Same rule as the worker: only same-origin paths are navigable from a notification. */
export const toSafeAppPath = (rawUrl) => {
  if (typeof rawUrl !== 'string' || !rawUrl) return '/';
  try {
    const url = new URL(rawUrl, window.location.origin);
    if (url.origin !== window.location.origin) return '/';
    return `${url.pathname}${url.search}${url.hash}`;
  } catch (_) {
    return '/';
  }
};
